import { QueueProject } from "in_queue/types/projectType";
import { ScenarioMetadata } from "in_queue/types/scenarioType";
import { applyHybridLimitToProject } from "in_queue/types/scenarioUtils";

/**
 * Coerce a possibly-null/undefined/non-finite assumption value into a
 * clean non-negative number. Assumptions can come in as null/undefined
 * from older scenarios and must not silently propagate NaN into chart
 * data or tooltips.
 */
export const safeEris = (v: unknown): number => {
    if (typeof v !== "number") return 0;
    if (!Number.isFinite(v)) return 0;
    return Math.max(0, v);
};

export interface ClusterMwMap {
    /** projectId -> capped ERIS MW (hybrid-limit-applied). */
    projectIdToCappedMw: Map<string, number>;
    /** Sum of capped ERIS MW across all queue projects. */
    totalMw: number;
}

/**
 * Build a {projectId -> capped ERIS MW} map plus the total cluster MW.
 *
 * "Capped" means we apply the project's hybrid limit (when present) so a
 * 100 MW solar project paired with a 50 MW battery contributes 50 MW to
 * the cluster total — the same number cost-allocation uses for `mw_impact`.
 *
 * Speculative-only projects passed in will get an entry if they happen
 * to be in `queueProjects`; downstream code should treat a missing entry
 * as "speculative or unknown and should be skipped".
 */
export const buildClusterMwMap = (
    queueProjects: ReadonlyArray<QueueProject>
): ClusterMwMap => {
    const projectIdToCappedMw = new Map<string, number>();
    let totalMw = 0;
    for (const p of queueProjects) {
        const cappedMw = applyHybridLimitToProject(p).erisMw;
        projectIdToCappedMw.set(p.projectId, cappedMw);
        totalMw += cappedMw;
    }
    return { projectIdToCappedMw, totalMw };
};

export interface ScenarioDropoff {
    /** This project's ERIS reduction in the scenario (0..100). */
    ownDownsizePct: number;
    /** This project's NRIS reduction in the scenario (0..100). */
    ownNrisDownsizePct: number;
    /** # of OTHER cluster projects with a non-zero ERIS reduction. */
    otherProjectCount: number;
    /** Sum of (capped MW × ERIS reduction) for those other projects. */
    otherDropoffMw: number;
    /** otherDropoffMw / totalClusterMw, in 0..1. */
    otherDropoffPct: number;
}

/**
 * Compute per-scenario "dropoff" stats relative to a current project.
 *
 * - `ownDownsizePct` reflects how much THIS project is downsized.
 * - The "other" stats reflect how much of the REST of the cluster is
 *   removed by the scenario's sensitivities. Projects without an entry
 *   in `mwMap` (e.g. speculative-only or unknown) are skipped — this
 *   matches the prior chart behavior of only counting MW we can size.
 */
export const computeScenarioDropoff = (
    scenario: ScenarioMetadata,
    currentProjectId: string,
    mwMap: ClusterMwMap
): ScenarioDropoff => {
    const ownDownsizePct = safeEris(
        scenario.assumptions[currentProjectId]?.eris
    );
    // NRIS reduction is tracked separately so the charts can recognize a
    // project as "modified" even on NRIS-only downsizes (where ERIS is
    // 0). It does NOT feed the cluster MW math below, which mirrors
    // cost allocation's ERIS-driven `mw_impact`.
    const ownNrisDownsizePct = safeEris(
        scenario.assumptions[currentProjectId]?.nris
    );

    let otherProjectCount = 0;
    let otherDropoffMw = 0;
    for (const [pid, r] of Object.entries(scenario.assumptions)) {
        if (pid === currentProjectId) continue;
        const cappedMw = mwMap.projectIdToCappedMw.get(pid);
        if (cappedMw === undefined) continue; // skip speculative / unknown
        const eris = safeEris(r?.eris);
        if (eris <= 0) continue;
        otherProjectCount += 1;
        otherDropoffMw += cappedMw * (eris / 100);
    }
    const otherDropoffPct =
        mwMap.totalMw > 0 ? otherDropoffMw / mwMap.totalMw : 0;

    return {
        ownDownsizePct,
        ownNrisDownsizePct,
        otherProjectCount,
        otherDropoffMw,
        otherDropoffPct
    };
};

/**
 * Format a project's own ERIS / NRIS reductions for the "Own downsize"
 * tooltip line on the project charts. Shows each non-zero service so an
 * NRIS-only downsize reads as "100% NRIS" instead of "—" (which the
 * ERIS-only check produced, mislabeling the project as unchanged).
 *
 *   (0, 0)     -> "—"
 *   (50, 0)    -> "50% ERIS"
 *   (0, 100)   -> "100% NRIS"
 *   (100, 50)  -> "100% ERIS · 50% NRIS"
 */
export const formatOwnDownsize = (
    ownDownsizePct: number,
    ownNrisDownsizePct: number
): string => {
    const parts: string[] = [];
    if (ownDownsizePct > 0) parts.push(`${ownDownsizePct.toFixed(0)}% ERIS`);
    if (ownNrisDownsizePct > 0) {
        parts.push(`${ownNrisDownsizePct.toFixed(0)}% NRIS`);
    }
    return parts.length === 0 ? "\u2014" : parts.join(" \u00b7 ");
};
